import { Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ContactRoundIcon } from 'lucide-react';
import { EmptyState, ErrorNotice, Panel, SkeletonTable } from '@bcecloud/portal-ui';
import { errorMessage, getJson } from '@/lib/api';
import type { ContactRecord } from '@/pages/contacts-shared';

/**
 * Email → directory redirect. Event pages link contacts by email (the only
 * identity an embedded contact has); this resolves the directory row and
 * forwards to /contacts/:id. A contact the sync hasn't materialized yet gets
 * an honest explanation instead of a broken page.
 */
export function ContactLookupPage(): React.ReactElement {
  const { email = '' } = useParams();
  const decoded = decodeURIComponent(email);

  const lookup = useQuery({
    queryKey: ['contact-by-email', decoded],
    queryFn: ({ signal }) =>
      getJson<ContactRecord>('/contacts/by-email', { email: decoded }, signal),
    enabled: decoded.includes('@'),
    retry: false,
  });

  if (lookup.isPending) return <SkeletonTable label="Finding contact…" />;
  if (lookup.isSuccess) return <Navigate to={`/contacts/${lookup.data.id}`} replace />;

  const status = (lookup.error as { response?: { status?: number } } | null)?.response?.status;
  return status === 404 ? (
    <Panel>
      <EmptyState
        icon={<ContactRoundIcon aria-hidden className="size-6" />}
        title="Not in the directory yet"
        message={`${decoded} appears on an event but hasn't been synced into the contact directory — the sync runs nightly, or run pnpm sync:contacts.`}
      />
    </Panel>
  ) : (
    <ErrorNotice title="Contact unavailable" message={errorMessage(lookup.error)} />
  );
}
